import type { PracticeMode, PracticeSession, UserDatabase } from '@/types/user-data.js'

export interface PracticeSessionInput {
  id?: string
  startedAt?: Date
  mode: PracticeMode
  category?: string
  questionIds: readonly string[]
}

function createRandomId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function createPracticeSessionId(): string {
  return `session-${createRandomId()}`
}

export function createQuestionAttemptId(sessionId: string, questionIndex: number): string {
  return `attempt-${sessionId}-${questionIndex}`
}

export function startPracticeSession(
  database: UserDatabase,
  input: PracticeSessionInput,
): UserDatabase {
  const startedAt = input.startedAt ?? new Date()
  const timestamp = startedAt.toISOString()
  const id = input.id ?? createPracticeSessionId()
  const questionIds = [...input.questionIds]

  if (!questionIds.length) throw new Error('练习 Session 至少需要一道题。')
  if (new Set(questionIds).size !== questionIds.length) {
    throw new Error('练习 Session 中的题目不能重复。')
  }
  if (database.practiceSessions[id]) {
    throw new Error(`练习 Session ${id} 已存在，不能覆盖。`)
  }

  const practiceSessions = Object.fromEntries(
    Object.entries(database.practiceSessions).map(([sessionId, session]) => [
      sessionId,
      session.finishedAt ? session : { ...session, finishedAt: timestamp },
    ]),
  ) as Record<string, PracticeSession>

  practiceSessions[id] = {
    id,
    startedAt: timestamp,
    mode: input.mode,
    ...(input.category ? { category: input.category } : {}),
    questionIds,
    attemptIds: [],
    totalCount: questionIds.length,
    answeredCount: 0,
    correctCount: 0,
    wrongCount: 0,
  }

  return { ...database, practiceSessions }
}

export function finishPracticeSession(
  database: UserDatabase,
  sessionId: string,
  finishedAt = new Date(),
): UserDatabase {
  const session = database.practiceSessions[sessionId]
  if (!session || session.finishedAt) return database
  return {
    ...database,
    practiceSessions: {
      ...database.practiceSessions,
      [sessionId]: { ...session, finishedAt: finishedAt.toISOString() },
    },
  }
}

export function findUnfinishedPracticeSession(database: UserDatabase): PracticeSession | undefined {
  return Object.values(database.practiceSessions)
    .filter((session) => !session.finishedAt)
    .sort((left, right) => right.startedAt.localeCompare(left.startedAt))[0]
}
